import { useEffect, type RefObject } from 'react';

/**
 * Custom hook to handle click-outside behavior for menu dropdowns
 * Closes the menu when clicking outside menu buttons and dropdown content
 *
 * @param activeMenuItemId - ID of the currently active/open menu item
 * @param setActiveMenuItemId - Function to set the active menu item (pass null to close)
 * @param menuButtonsRef - Ref containing Set of menu button elements
 * @param dropdownContentsRef - Ref containing Set of dropdown content elements
 */
const useMenuClickOutside = (
  activeMenuItemId: string | null,
  setActiveMenuItemId: (id: string | null) => void,
  menuButtonsRef: RefObject<Set<HTMLElement>>,
  dropdownContentsRef: RefObject<Set<HTMLElement>>
) => {
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (activeMenuItemId) {
        const target = event.target as Node;

        // Check if click is within any menu button or dropdown content
        const isWithinMenuButton = Array.from(menuButtonsRef.current || []).some((el) =>
          el?.contains(target)
        );
        const isWithinDropdownContent = Array.from(dropdownContentsRef.current || []).some((el) =>
          el?.contains(target)
        );

        // Close if clicking outside menu buttons and dropdown content
        if (!isWithinMenuButton && !isWithinDropdownContent) {
          setActiveMenuItemId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [activeMenuItemId, setActiveMenuItemId, menuButtonsRef, dropdownContentsRef]);
};

export default useMenuClickOutside;
